import React, { useEffect, useCallback, useRef, useState } from 'react';
import { TextField, Select, MenuItem, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import notify from 'lib/notify';

export type PhoneItem = {
  phone: string;
  type: string;
};

type InlineConfigBase<V> = {
  value: V;
  onChange: (v: V) => void;
  onCommit: (v: V) => Promise<boolean> | void;
  onCancel: () => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
};

type PhonesInlineConfig = InlineConfigBase<PhoneItem[]> & {
  selectZIndex?: number;
  onSaveField?: (fieldName: string, value: unknown) => Promise<void>;
  initialPrimaryPhone?: string;
};

const primaryIconStyle = { height: 20, width: 20, cursor: 'pointer' };

const emptyPhoneRow = (): PhoneItem => ({ phone: '', type: 'personal' });

const ensureEmptyRow = (rows: PhoneItem[]): PhoneItem[] =>
  rows.some(r => r.phone === '') ? rows : [...rows, emptyPhoneRow()];

const nonEmpty = (rows: PhoneItem[]): PhoneItem[] => rows.filter(r => r.phone !== '');

const EMPTY_PHONE_ARRAY: PhoneItem[] = [];

const PhonesManager = ({
  initialPhones = EMPTY_PHONE_ARRAY,
  initialPrimaryPhone,
  onChange,
  onChangePrimaryPhone,
  triggerSave,
  inlineConfig,
}: {
  initialPhones?: PhoneItem[];
  initialPrimaryPhone?: string;
  onChange?: (phones: PhoneItem[]) => void;
  onChangePrimaryPhone?: (primaryPhone?: string) => void;
  triggerSave?: () => void;
  inlineConfig?: PhonesInlineConfig;
}) => {
  const isInline = !!inlineConfig;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const phonesRef = useRef<PhoneItem[]>([]);

  // ---------- Non-inline state ----------
  const [phones, setPhones] = useState<PhoneItem[]>(() => ensureEmptyRow(initialPhones ?? []));
  const [primaryPhone, setPrimaryPhone] = useState<string | null | undefined>(initialPrimaryPhone);
  phonesRef.current = phones;

  // ---------- Inline state ----------
  const [inlinePhones, setInlinePhones] = useState<PhoneItem[]>(() =>
    ensureEmptyRow(inlineConfig?.value?.length ? [...inlineConfig.value] : []),
  );
  const [inlinePrimaryPhone, setInlinePrimaryPhone] = useState<string | null | undefined>(
    inlineConfig?.initialPrimaryPhone ?? null,
  );
  const [inlinePrimaryIndex, setInlinePrimaryIndex] = useState<number | null>(() => {
    if (!inlineConfig?.initialPrimaryPhone) return null;
    return (inlineConfig?.value || []).findIndex(p => p.phone === inlineConfig.initialPrimaryPhone);
  });

  useEffect(() => {
    if (isInline && inlineConfig?.autoFocus !== false) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isInline, inlineConfig?.autoFocus]);

  const menuProps = React.useMemo(() => {
    if (!isInline) return undefined;
    return { sx: { zIndex: inlineConfig?.selectZIndex } };
  }, [isInline, inlineConfig?.selectZIndex]);

  const handleSelectMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const currentPhones = isInline ? inlinePhones : phones;
  const currentPrimaryPhone = isInline ? inlinePrimaryPhone : primaryPhone;

  const getPhones = (newPhones: PhoneItem[]) => newPhones.filter(phone => phone.phone !== '');

  const addEmptyInput = useCallback(() => {
    if (isInline) {
      setInlinePhones(prev => ensureEmptyRow([...prev]));
    } else {
      if (!phonesRef.current.some(p => p.phone === '')) {
        setPhones(prev => [...prev, emptyPhoneRow()]);
      }
    }
  }, [isInline]);

  useEffect(() => {
    if (!isInline) {
      addEmptyInput();
    }
  }, [initialPhones, addEmptyInput, isInline]);

  const removePhone = (index: number) => {
    if (isInline) {
      const newPhones = inlinePhones.filter((_, i) => i !== index);
      if (inlinePrimaryIndex != null) {
        if (index === inlinePrimaryIndex) {
          setInlinePrimaryIndex(null);
        } else if (index < inlinePrimaryIndex) {
          setInlinePrimaryIndex(inlinePrimaryIndex - 1);
        }
      }
      const withEmpty = ensureEmptyRow(newPhones);
      setInlinePhones(withEmpty);
      inlineConfig?.onChange(nonEmpty(withEmpty));
    } else {
      const newPhones = phones.filter((_, i) => i !== index);
      if (primaryPhone && !newPhones.some(p => p.phone === primaryPhone)) {
        setPrimaryPhone(null);
        onChangePrimaryPhone?.(undefined);
      }
      setPhones(newPhones);
      onChange?.(getPhones(newPhones));
    }
  };

  const onInputChange = (index: number, value: string) => {
    if (value === '') {
      removePhone(index);
      return;
    }

    if (isInline) {
      const newPhones = [...inlinePhones];
      const newPhone = { phone: value, type: newPhones[index]?.type ?? 'personal' };
      newPhones[index] = newPhone;
      const withEmpty = ensureEmptyRow(newPhones);
      setInlinePhones(withEmpty);
      inlineConfig?.onChange(nonEmpty(withEmpty));

      // Sync primary when editing the current primary phone's text
      if (inlinePrimaryIndex === index && newPhone.phone !== '') {
        setInlinePrimaryPhone(newPhone.phone);
      }
    } else {
      const newPhones = [...phones];
      newPhones[index] = { phone: value, type: newPhones[index].type };
      setPhones(newPhones);
      onChange?.(getPhones(newPhones));
    }
  };

  const onTypeChange = (index: number, typeValue: string) => {
    if (isInline) {
      const newPhones = [...inlinePhones];
      newPhones[index] = { ...newPhones[index], type: typeValue };
      const withEmpty = ensureEmptyRow(newPhones);
      setInlinePhones(withEmpty);
      inlineConfig?.onChange(nonEmpty(withEmpty));
    } else {
      const newPhones = [...phones];
      newPhones[index] = { ...newPhones[index], type: typeValue };
      setPhones(newPhones);
      onChange?.(getPhones(newPhones));
    }
  };

  const handleSetPrimary = async (index: number) => {
    const phone = currentPhones[index]?.phone;
    if (!phone) return;
    if (isInline) {
      const prevIndex = inlinePrimaryIndex;
      const prevPhone = inlinePrimaryPhone;
      setInlinePrimaryIndex(index);
      setInlinePrimaryPhone(phone);
      try {
        await inlineConfig?.onSaveField?.('phones', nonEmpty(ensureEmptyRow(inlinePhones)));
        await inlineConfig?.onSaveField?.('primaryPhone', phone);
      } catch (error) {
        notify(error);
        setInlinePrimaryIndex(prevIndex);
        setInlinePrimaryPhone(prevPhone);
      }
    } else {
      setPrimaryPhone(phone);
      onChangePrimaryPhone?.(phone);
    }
  };

  const handleClearPrimary = async () => {
    if (isInline) {
      const prevIndex = inlinePrimaryIndex;
      const prevPhone = inlinePrimaryPhone;
      setInlinePrimaryIndex(null);
      setInlinePrimaryPhone(null);
      try {
        await inlineConfig?.onSaveField?.('phones', nonEmpty(ensureEmptyRow(inlinePhones)));
        await inlineConfig?.onSaveField?.('primaryPhone', null);
      } catch (error) {
        notify(error);
        setInlinePrimaryIndex(prevIndex);
        setInlinePrimaryPhone(prevPhone);
      }
    } else {
      setPrimaryPhone(null);
      onChangePrimaryPhone?.(undefined);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (isInline) {
        await inlineConfig?.onCommit?.(nonEmpty(ensureEmptyRow(inlinePhones)));
      } else if (triggerSave) {
        triggerSave();
      }
    } else if (e.key === 'Escape') {
      if (isInline) {
        e.stopPropagation();
        e.preventDefault();
        inlineConfig?.onCancel();
      }
    }
  };

  return (
    <>
      {currentPhones.map((phone, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Select
            value={phone.type}
            onChange={e => onTypeChange(index, e.target.value)}
            sx={{ width: '110px' }}
            size="small"
            disabled={isInline ? inlineConfig?.disabled : false}
            MenuProps={menuProps}
            onMouseDown={handleSelectMouseDown}
          >
            <MenuItem value="personal">Personal</MenuItem>
            <MenuItem value="work">Work</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>

          <TextField
            inputRef={index === 0 ? inputRef : undefined}
            sx={{ flex: '1' }}
            value={phone.phone}
            onChange={e => onInputChange(index, e.target.value)}
            {...(!isInline && { onFocus: addEmptyInput, onBlur: () => addEmptyInput() })}
            size="small"
            onKeyDown={handleKeyDown}
            disabled={isInline ? inlineConfig?.disabled : false}
            error={isInline ? inlineConfig?.hasError : false}
          />

          {isInline
            ? inlinePrimaryIndex === index
              ? (<CheckCircleIcon style={primaryIconStyle} onClick={handleClearPrimary} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }} />)
              : (
                <Tooltip title="Set as Primary">
                  <RadioButtonUncheckedIcon
                    style={primaryIconStyle}
                    onClick={() => handleSetPrimary(index)}
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                  />
                </Tooltip>
              )
            : currentPrimaryPhone === phone.phone
              ? (<CheckCircleIcon style={primaryIconStyle} onClick={handleClearPrimary} />)
              : (
                <Tooltip title="Set as Primary">
                  <RadioButtonUncheckedIcon
                    style={primaryIconStyle}
                    onClick={() => handleSetPrimary(index)}
                  />
                </Tooltip>
              )}
        </div>
      ))}
    </>
  );
};

export default PhonesManager;
